import { Component, TemplateFunction } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { ShopifyCartService } from '../services/index.js'
import { TwSlideshowComponent } from '../modules/tailwind/components'
import type {
  SbSidebarComponentScope,
  SidebarState,
  ShopifyCartObject,
  ShopifyCartAction,
} from '../types/index.js'
import type { JsxSbSidebarProps } from '../types/jsx'
import type { TouchSwipeData } from '@ribajs/extras'

/**
 * A custom sidebar element with different modes, can be used left or right
 */
export class SbSidebarComponent extends Component {
  public static tagName = 'sb-sidebar'

  slideshow?: TwSlideshowComponent

  protected computedStyle?: CSSStyleDeclaration

  /** Listen for client side routing events */
  protected routerEvents = new EventDispatcher('main')

  static get observedAttributes(): (keyof JsxSbSidebarProps)[] {
    return [
      'id',
      'container-selector',
      'position',
      'mode',
      'width',
      'disable-page-scroll',
      'open-on-cart-update',
    ]
  }

  protected requiredAttributes(): string[] {
    return []
  }

  public defaults: SbSidebarComponentScope = {
    // Template properties
    containerSelector: undefined,
    state: 'hidden',
    oldState: 'hidden',
    id: undefined,
    width: '',

    // Options
    position: 'left',
    mode: 'overlap',
    disablePageScroll: false,
    openOnCartUpdate: false,

    // Template methods
    hide: this.hide,
    show: this.show,
    toggle: this.toggle,
  }

  public scope: SbSidebarComponentScope = {
    ...this.defaults,
  }

  public setState(state: SidebarState) {
    this.scope.oldState = this.scope.state
    this.scope.state = state
    this.onStateChange()
  }

  public getState() {
    return this.scope.state
  }

  public getShowMode() {
    const mode: SidebarState =
      `${this.scope.mode}-${this.scope.position}` as SidebarState
    return mode
  }

  public hide() {
    this.setState('hidden')
  }

  public show() {
    const state = this.getShowMode()
    this.setState(state)
  }

  public toggle() {
    this.debug('toggle state: ' + this.scope.state)
    if (this.scope.state === 'hidden') {
      this.show()
    } else {
      this.hide()
    }
    this.debug('toggled state: ' + this.scope.state)
  }

  protected onCartRequestStart() {
    //
  }

  protected async onCartRequestComplete(
    cart: ShopifyCartObject,
    action: ShopifyCartAction,
  ) {
    if (
      this.scope.openOnCartUpdate &&
      action === 'add' &&
      this.scope.state === 'hidden' &&
      window.location.pathname !== '/cart'
    ) {
      this.show()
    }
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbSidebarComponent.observedAttributes)
    this.computedStyle = window.getComputedStyle(this)
  }

  protected onNewPageReady() {
    this.hide()
  }

  protected addEventListeners() {
    // Needs the extras event binder from @ribajs/extras
    this.addEventListener('swipe' as any, this.onSwipe)

    this.routerEvents.on('newPageReady', this.onNewPageReady, this)

    ShopifyCartService.events.on(
      'ShopifyCart:request:start',
      this.onCartRequestStart,
      this,
    )

    ShopifyCartService.events.on(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected removeEventListeners() {
    this.removeEventListener('swipe' as any, this.onSwipe)
    // Ensure keydown listener is detached
    window.removeEventListener('keydown', this.onKeyDown)

    this.routerEvents.off('newPageReady', this.onNewPageReady, this)

    ShopifyCartService.events.off(
      'ShopifyCart:request:start',
      this.onCartRequestStart,
      this,
    )

    ShopifyCartService.events.off(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected _onSwipe(event: CustomEvent<TouchSwipeData>) {
    if (this.scope.state === 'side-left' || this.scope.state === 'side-right') {
      return
    }
    if (this.scope.position === 'left' && event.detail.direction === 'left') {
      this.hide()
    }

    if (this.scope.position === 'right' && event.detail.direction === 'right') {
      this.hide()
    }
  }

  protected onSwipe = this._onSwipe.bind(this)

  protected onHidden() {
    const translateX = this.scope.position === 'left' ? '-100%' : '100%'
    this.style.transform = `translateX(${translateX})`

    // Hide sidebar after animation (300ms) is done
    setTimeout(() => {
      if (this.scope.state === 'hidden') {
        this.style.display = 'none'
      }
    }, 300)

    this.width = this.scope.width
    this.setContainersStyle(this.scope.state)
  }

  protected onVisible() {
    this.style.display = 'block'
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '-1')
    }
    this.focus({ preventScroll: true })
  }

  protected onMove(state: SidebarState) {
    this.onVisible()

    setTimeout(() => {
      this.style.transform = `translateX(0)`
      this.style.width = this.scope.width
      this.width = this.scope.width
      this.setContainersStyle(state)
    }, 50)
  }

  protected onSide(state: SidebarState) {
    this.onVisible()

    setTimeout(() => {
      this.style.transform = `translateX(0)`
      this.width = this.scope.width
      this.setContainersStyle(state)
    }, 50)
  }

  protected onOverlap(state: SidebarState) {
    this.onVisible()

    setTimeout(() => {
      this.style.transform = `translateX(0)`
      this.width = this.scope.width
      this.setContainersStyle(state)
    }, 50)
  }

  protected _onKeyDown(event: KeyboardEvent) {
    switch (event.code) {
      case 'Escape':
        this.hide()
        break
    }
  }
  protected onKeyDown = this._onKeyDown.bind(this)

  protected updateKeydownListener() {
    if (this.scope.state !== 'hidden') {
      window.addEventListener('keydown', this.onKeyDown, { passive: true })
    } else {
      window.removeEventListener('keydown', this.onKeyDown)
    }
  }

  protected triggerState() {
    // Local element event
    this.dispatchEvent(
      new CustomEvent('state-changed', {
        detail: this.scope.state,
      }),
    )
    // Global event
    EventDispatcher.getInstance().trigger('sidebar-state-changed', {
      state: this.scope.state,
      sidebar: this,
    })
  }

  protected onStateChange() {
    switch (this.scope.state) {
      case 'side-left':
      case 'side-right':
        this.onSide(this.scope.state)
        break
      case 'overlap-left':
      case 'overlap-right':
        this.onOverlap(this.scope.state)
        break
      case 'move-left':
      case 'move-right':
        this.onMove(this.scope.state)
        break
      default:
        this.onHidden()
        break
    }
    this.updatePageScroll()
    this.updateKeydownListener()
    this.triggerState()
    if (this.scope.state !== 'hidden') {
      this.updateSlideshow()
    }
  }

  protected disablePageScroll() {
    document.body.style.overflowY = 'hidden'
  }

  protected enablePageScroll() {
    document.body.style.overflowY = ''
  }

  protected updatePageScroll() {
    if (!this.scope.disablePageScroll) {
      return
    }
    if (this.scope.state === 'hidden') {
      this.enablePageScroll()
    } else {
      this.disablePageScroll()
    }
  }

  public get width() {
    if (this.scope.width === this.defaults.width) {
      return this.offsetWidth + 'px'
    }
    return this.scope.width
  }

  public set width(width: string) {
    if (width) {
      this.scope.width = width
    }
    if (this.scope.width) {
      this.style.width = this.scope.width
    }
  }

  protected getContainers() {
    return this.scope.containerSelector
      ? document.querySelectorAll<HTMLUnknownElement>(
          this.scope.containerSelector,
        )
      : undefined
  }

  protected initContainers(state: SidebarState) {
    this.setContainersStyle(state)
  }

  protected setContainersStyle(state: SidebarState) {
    const containers:
      | NodeListOf<HTMLUnknownElement>
      | Array<HTMLUnknownElement> = this.getContainers() || []

    if (containers) {
      for (let i = 0; i < containers.length; i++) {
        const container = containers[i]
        this.setContainerStyle(container, state)
      }
    }
  }

  /**
   * Sets the container style and always uses the sidebar's transition style.
   * @param container The container element to adjust
   * @param state The current sidebar state
   */
  protected setContainerStyle(
    container: HTMLUnknownElement,
    state: SidebarState,
  ) {
    const currStyle = container.style
    if (state) {
      const width = this.width
      const conStyle = window.getComputedStyle(container)

      if (this.scope.mode === 'move' && state.startsWith('overlap-')) {
        switch (conStyle.position) {
          case 'fixed':
          case 'absolute':
            currStyle.left = '0'
            currStyle.right = '0'
            break
          default:
            currStyle.marginLeft = '0'
            currStyle.marginRight = '0'
            break
        }
      }

      switch (state) {
        case 'side-left':
          switch (conStyle.position) {
            case 'fixed':
            case 'absolute':
              currStyle.left = width
              break
            default:
              currStyle.marginLeft = width
              break
          }
          break
        case 'side-right':
          switch (conStyle.position) {
            case 'fixed':
            case 'absolute':
              currStyle.right = width
              break
            default:
              currStyle.marginRight = width
              break
          }
          break
        case 'move-left':
          switch (conStyle.position) {
            case 'fixed':
            case 'absolute':
              currStyle.left = width
              currStyle.right = `-${width}`
              break
            default:
              currStyle.marginLeft = width
              currStyle.marginRight = `-${width}`
              break
          }
          break
        case 'move-right':
          switch (conStyle.position) {
            case 'fixed':
            case 'absolute':
              currStyle.right = width
              currStyle.left = `-${width}`
              break
            default:
              currStyle.marginRight = width
              currStyle.marginLeft = `-${width}`
              break
          }
          break
        case 'hidden':
          switch (this.scope.oldState) {
            case 'side-left':
              switch (conStyle.position) {
                case 'fixed':
                case 'absolute':
                  currStyle.left = '0'
                  break
                default:
                  currStyle.marginLeft = '0'
                  break
              }
              break
            case 'side-right':
              switch (conStyle.position) {
                case 'fixed':
                case 'absolute':
                  currStyle.right = '0'
                  break
                default:
                  currStyle.marginRight = '0'
                  break
              }
              break
            case 'move-left':
              switch (conStyle.position) {
                case 'fixed':
                case 'absolute':
                  currStyle.left = '0'
                  currStyle.right = '0'
                  break
                default:
                  currStyle.marginRight = '0'
                  currStyle.marginLeft = '0'
                  break
              }
              break
            case 'move-right':
              switch (conStyle.position) {
                case 'fixed':
                case 'absolute':
                  currStyle.right = '0'
                  currStyle.left = '0'
                  break
                default:
                  currStyle.marginRight = '0'
                  currStyle.marginLeft = '0'
                  break
              }
              break
            default:
              break
          }

        default:
          break
      }
    }
    container.style.transition = this.computedStyle
      ? this.computedStyle.transition
      : ''
  }

  protected async beforeBind() {
    await super.beforeBind()
    this.scope.oldState = this.getShowMode()
    const state = this.getState()
    if (state === 'hidden') {
      this.onHidden()
    } else {
      this.onVisible()
    }
    // Ensure keydown listener reflects the initial state
    this.updateKeydownListener()
  }

  protected async afterBind() {
    await super.afterBind()
    this.addEventListeners()
    this.updateSlideshow()
    this.classList.add(`${SbSidebarComponent.tagName}-ready`)
  }

  /**
   * Update swiper if the sidebar contains a slideshow
   * @returns
   */
  protected updateSlideshow() {
    this.slideshow =
      this.querySelector(TwSlideshowComponent.tagName) || undefined
    if (!this.slideshow) {
      return
    }
    // Check if the slideshow is already initialized
    if (typeof this.slideshow.updateItems === 'function') {
      this.slideshow.updateItems()
    }
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ) {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    switch (attributeName) {
      case 'containerSelector':
        this.initContainers(this.scope.state)
        break
      case 'width':
        this.width = newValue
        break
      case 'mode':
        this.onStateChange()
        this.initContainers(this.scope.state)
        break
      default:
        break
    }
  }

  // deconstruction
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
